import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarHeart, Star, Globe } from "lucide-react";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

export const Route = createFileRoute("/_authenticated/therapists")({
  component: TherapistsPage,
});

function TherapistsPage() {
  const qc = useQueryClient();
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const { openCheckout, closeCheckout, isOpen, checkoutElement } = useStripeCheckout();

  const { data: therapists } = useQuery({
    queryKey: ["therapists"],
    queryFn: async () => (await supabase.from("therapists").select("*").eq("available", true)).data ?? [],
  });

  const { data: bookings } = useQuery({
    queryKey: ["bookings"],
    queryFn: async () => (await supabase.from("bookings").select("*, therapists(name, specialty)").order("scheduled_at", { ascending: true })).data ?? [],
  });

  const book = async () => {
    if (!pickedId || !when) { toast.error("Pick a date and time"); return; }
    openCheckout({
      kind: "booking",
      therapistId: pickedId,
      scheduledAt: new Date(when).toISOString(),
      notes: notes || undefined,
      returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
    });
    // Reset selection after handing off to checkout; booking is created on payment success via webhook.
    setPickedId(null); setWhen(""); setNotes("");
    qc.invalidateQueries({ queryKey: ["bookings"] });
  };

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-3xl text-gradient">Find your therapist</h1>
        <p className="text-sm text-muted-foreground mt-1">Verified, licensed clinicians. Book at your pace.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {therapists?.map((t) => (
          <div key={t.id} className="glass rounded-3xl p-6 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-xl">{t.name}</h3>
                <div className="text-sm text-accent flex items-center gap-1.5"><Star className="h-3 w-3" />{t.specialty}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">per session</div>
                <div className="font-medium">${t.rate_per_session}</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.bio}</p>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Globe className="h-3 w-3" />{t.languages.join(", ")}</div>
            <Button variant={pickedId === t.id ? "default" : "secondary"} onClick={() => setPickedId(t.id)} className="w-full">
              {pickedId === t.id ? "Selected — fill below" : "Book a session"}
            </Button>
          </div>
        ))}
      </div>

      {pickedId && !isOpen && (
        <div className="glass rounded-3xl p-6 space-y-4 border-primary/40">
          <h2 className="text-xl flex items-center gap-2"><CalendarHeart className="h-5 w-5 text-accent" />New session request</h2>
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          <Textarea placeholder="Anything you'd like the therapist to know? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <div className="flex gap-2">
            <Button onClick={book} className="flex-1 glow-primary">Continue to payment</Button>
            <Button variant="ghost" onClick={() => setPickedId(null)}>Cancel</Button>
          </div>
          <p className="text-xs text-muted-foreground">Your booking is confirmed once payment completes.</p>
        </div>
      )}

      {isOpen && (
        <div className="glass rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl">Complete payment</h2>
            <Button variant="ghost" size="sm" onClick={closeCheckout}>Close</Button>
          </div>
          {checkoutElement}
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-xl">Your sessions</h2>
        {bookings && bookings.length > 0 ? (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="glass rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{(b.therapists as { name?: string } | null)?.name ?? "Therapist"}</div>
                  <div className="text-xs text-muted-foreground">{(b.therapists as { specialty?: string } | null)?.specialty}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm">{format(new Date(b.scheduled_at), "MMM d, h:mm a")}</div>
                  <div className="text-xs text-accent capitalize">{b.status} · {b.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 text-center text-sm text-muted-foreground">No sessions yet.</div>
        )}
      </div>
    </div>
  );
}
